import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button, Switch } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaTimes, FaPenAlt } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
const { rootUrl } = require('../../../config/config');

const PartSuppliers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    code: '',
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    payment_terms: '',
    is_preferred: false,
    is_active: true
  });
  const { user } = useAuth();

  // Fetch all part suppliers
  const fetchItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || 'default-token';
      const response = await axios.get(`${rootUrl}/parts/suppliers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch part suppliers.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Start editing a row - populate form with item data
  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      code: item.code || '',
      name: item.name || '',
      contact_person: item.contact_person || '',
      phone: item.phone || '',
      email: item.email || '',
      address: item.address || '',
      payment_terms: item.payment_terms || '',
      is_preferred: !!item.is_preferred,
      is_active: !!item.is_active
    });
    // Scroll to top to show the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cancel editing/adding - clear form
  const cancelForm = () => {
    setEditingId(null);
    setForm({
      code: '',
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      payment_terms: '',
      is_preferred: false,
      is_active: true
    });
    setError('');
    setErrors({});
  };

  // Delete an item
  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this part supplier?')) return;
    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      await axios.delete(`${rootUrl}/parts/suppliers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchItems();
    } catch (err) {
      setError('Failed to delete part supplier.');
      console.error(err);
    }
  };

  // Validate form data
  const validateForm = () => {
    let newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Supplier name is required";
    }

    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email must be a valid email address";
    }

    if (form.phone && !/^\d{10,15}$/.test(form.phone.replace(/[\s\-()]/g, ''))) {
      newErrors.phone = "Phone must be 10-15 digits";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  // Handle form submission for both add and edit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || 'default-token';

      if (editingId) {
        // Update existing item (do not send code)
        const { code, ...rest } = form;
        await axios.put(
          `${rootUrl}/parts/suppliers/${editingId}`,
          {
            ...rest,
            edit_by: user?.username || 'system'
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        await axios.post(
          `${rootUrl}/parts/suppliers`,
          {
            ...form,
            input_by: user?.username || 'system'
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      cancelForm();
      fetchItems();
    } catch (err) {
      setError(editingId
        ? 'Failed to update part supplier.'
        : 'Failed to add part supplier.');
      console.error(err);
    }
  };

  // Inline toggle for preferred/status
  const handleInlineToggle = async (id, field, value) => {
    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      await axios.put(
        `${rootUrl}/parts/suppliers/${id}`,
        {
          [field]: value,
          edit_by: user?.username || 'system'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchItems();
    } catch (err) {
      console.error('Failed to toggle field', field, err);
      setError('Failed to update supplier.');
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
      <Card className="border-t">
        <CardBody className="p-3 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">
            Part Suppliers
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Add/Edit Form */}
          <form onSubmit={handleSubmit} className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">
                {editingId ? 'Edit Supplier' : 'Add New Supplier'}
              </h3>
              {editingId && (
                <Button
                  type="button"
                  variant="outlined"
                  color="gray"
                  onClick={cancelForm}
                  className="flex items-center shadow-none rounded-none"
                >
                  <FaTimes className="mr-2" /> Cancel
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  type="text"
                  label={editingId ? "Code *" : "Code (auto-generated)"}
                  value={form.code}
                  onChange={(e) => {
                    setForm({ ...form, code: e.target.value.toUpperCase() });
                    if (errors.code) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.code;
                        return newErrors;
                      });
                    }
                  }}

                  style={{ textTransform: 'uppercase' }}
                  required={editingId ? true : false}
                  readOnly={true}
                  disabled={true}
                  variant="outlined"
                  className="uppercase bg-gray-100 cursor-not-allowed"
                  placeholder={editingId ? "" : "Leave empty for auto-generation (SUP-001, SUP-002, etc.)"}
                />
                {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
              </div>
              <div>
                <Input
                  type="text"
                  label="Name *"
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                    if (errors.name) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.name;
                        return newErrors;
                      });
                    }
                  }}
                  required
                  placeholder="Enter supplier name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <Input
                type="text"
                label="Contact Person"
                value={form.contact_person}
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                placeholder="Contact person name"
              />
              <div>
                <Input
                  type="tel"
                  label="Phone"
                  value={form.phone}
                  onChange={(e) => {
                    setForm({ ...form, phone: e.target.value });
                    if (errors.phone) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.phone;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Phone number (10-15 digits)"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
              <div>
                <Input
                  type="email"
                  label="Email"
                  value={form.email}
                  onChange={(e) => {
                    setForm({ ...form, email: e.target.value });
                    if (errors.email) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.email;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="supplier@email.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              <Input
                type="text"
                label="Payment Terms"
                value={form.payment_terms}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
              />
            </div>
            <Input
              type="text"
              label="Address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mb-4"
            />
            {/* Modern switches positioned at the bottom of the form */}
            <div className="flex items-center gap-8 mb-4">
              <Switch
                id="is_preferred"
                checked={form.is_preferred}
                onChange={(e) => setForm({ ...form, is_preferred: e.target.checked })}
                label={<span className="text-sm text-gray-700">Preferred Supplier</span>}
              />
              <Switch
                id="is_active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                label={<span className="text-sm text-gray-700">Active</span>}
              />
            </div>
            <Button
              type="submit"
              className="bg-brand-primary text-white flex items-center shadow-none rounded-none"
            >
              <FaPlus className="mr-2" /> {editingId ? 'Update Supplier' : 'Add Supplier'}
            </Button>
          </form>

          {/* Items Table */}
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full table-auto border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm min-w-[120px]">Code</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Name</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden md:table-cell">Contact Person</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden lg:table-cell">Phone</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden lg:table-cell">Email</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden xl:table-cell">Payment Terms</th>
                    <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Preferred</th>
                    <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Status</th>
                    <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="border px-2 sm:px-4 py-2">
                        <Typography className="uppercase text-xs sm:text-sm">{item.code}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2">
                        <Typography className="text-xs sm:text-sm">{item.name}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 hidden md:table-cell">
                        <Typography className="text-xs sm:text-sm">{item.contact_person || '-'}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 hidden lg:table-cell">
                        <Typography className="text-xs sm:text-sm">{item.phone || '-'}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 hidden lg:table-cell">
                        <Typography className="text-xs sm:text-sm">{item.email || '-'}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 hidden xl:table-cell">
                        <Typography className="text-xs sm:text-sm">{item.payment_terms || '-'}</Typography>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleInlineToggle(item.id, 'is_preferred', !item.is_preferred)}
                          className={`px-2 sm:px-3 py-1 text-xs font-medium transition rounded-none shadow-none ${item.is_preferred
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
                            }`}
                        >
                          {item.is_preferred ? 'Preferred' : 'Standard'}
                        </button>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleInlineToggle(item.id, 'is_active', !item.is_active)}
                          className={`px-2 sm:px-3 py-1 text-xs font-medium transition rounded-none shadow-none ${item.is_active
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
                            }`}
                        >
                          {item.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="border px-2 sm:px-4 py-2 text-center">
                        <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                          <Button
                            onClick={() => startEdit(item)}
                            className="bg-blue-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none"
                            size="sm"
                            disabled={editingId === item.id}
                          >
                            <FaPenAlt className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                          </Button>
                          <Button
                            onClick={() => deleteItem(item.id)}
                            className="bg-red-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none"
                            size="sm"
                            disabled={editingId === item.id}
                          >
                            <FaTrash className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default PartSuppliers;

